import { useState, useEffect, useCallback, useRef } from 'react'
import { api } from '@/lib/api'
import { isSafeRedirectUrl } from '@/lib/security'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Progress } from '@/components/ui/progress'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { toast } from 'sonner'
import {
  Download,
  FileArchive,
  Loader2,
  RefreshCw,
  Trash2,
  Package,
  CalendarRange,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Clock,
  Info,
  Copy,
  ExternalLink,
} from 'lucide-react'

interface ExportJob {
  id: string
  export_type: string
  format: string
  status: string
  percentage: number
  messages_total: number | null
  messages_processed: number
  mailbox_email: string | null
  domain_name: string | null
  download_url?: string
  download_expires_at?: string
  file_size_mb?: number
  checksum_sha256?: string
  fail_reason?: string | null
  created_at: string
  completed_at: string | null
  failed_at: string | null
}

interface Domain {
  id: string
  name: string
}

interface Mailbox {
  id: string
  email: string
}

const EXPORT_TYPES = [
  { value: 'emails', label: 'Emails' },
  { value: 'contacts', label: 'Contacts' },
  { value: 'domain_settings', label: 'Domain Settings' },
]

const FORMATS: Record<string, { value: string; label: string }[]> = {
  emails: [
    { value: 'mbox', label: 'MBOX' },
    { value: 'eml', label: 'EML (ZIP)' },
    { value: 'maildir', label: 'Maildir (ZIP)' },
  ],
  contacts: [
    { value: 'vcf', label: 'VCF' },
  ],
  domain_settings: [
    { value: 'json', label: 'JSON' },
  ],
}

function statusIcon(status: string) {
  switch (status) {
    case 'complete':
      return <CheckCircle2 className="h-4 w-4 text-green-400" />
    case 'failed':
      return <XCircle className="h-4 w-4 text-red-400" />
    case 'processing':
      return <Loader2 className="h-4 w-4 text-amber-400 animate-spin" />
    case 'cancelled':
      return <AlertCircle className="h-4 w-4 text-muted-foreground" />
    default:
      return <Clock className="h-4 w-4 text-muted-foreground" />
  }
}

function statusBadge(status: string) {
  const variants: Record<string, string> = {
    pending: 'bg-muted text-muted-foreground',
    processing: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
    complete: 'bg-green-500/10 text-green-400 border-green-500/20',
    failed: 'bg-red-500/10 text-red-400 border-red-500/20',
    cancelled: 'bg-muted text-muted-foreground',
  }
  return (
    <Badge variant="outline" className={variants[status] || variants.pending}>
      {status}
    </Badge>
  )
}

function formatBytes(mb: number | undefined) {
  if (mb === undefined || mb === null) return null
  if (mb < 1) return `${(mb * 1024).toFixed(1)} KB`
  if (mb < 1024) return `${mb.toFixed(2)} MB`
  return `${(mb / 1024).toFixed(2)} GB`
}

interface ExportsProps {
  embedded?: boolean
  allowDelete?: boolean
}

export function Exports({ embedded, allowDelete = true }: ExportsProps) {
  const [exports, setExports] = useState<ExportJob[]>([])
  const [loading, setLoading] = useState(true)
  const [creating, setCreating] = useState(false)
  const [domains, setDomains] = useState<Domain[]>([])
  const [mailboxes, setMailboxes] = useState<Mailbox[]>([])
  const [selectedExport, setSelectedExport] = useState<ExportJob | null>(null)
  const [form, setForm] = useState({
    export_type: 'emails',
    format: 'mbox',
    mailbox_id: '',
    domain_id: '',
    date_from: '',
    date_to: '',
  })
  const loadErrorRef = useRef(false)

  const fetchExports = useCallback(async () => {
    try {
      const res = await api.get('/exports')
      setExports(res.data.exports || [])
      loadErrorRef.current = false
    } catch {
      if (!loadErrorRef.current) {
        toast.error('Failed to load exports')
        loadErrorRef.current = true
      }
    } finally {
      setLoading(false)
    }
  }, [])

  const fetchDomains = async () => {
    try {
      const res = await api.get('/domains')
      setDomains(res.data.domains || [])
    } catch {
      toast.error('Failed to load domains')
    }
  }

  const fetchMailboxes = async (domainId: string) => {
    try {
      const res = await api.get(`/mailboxes?domain_id=${domainId}&per_page=100`)
      setMailboxes(res.data.mailboxes || [])
    } catch {
      toast.error('Failed to load mailboxes')
      setMailboxes([])
    }
  }

  useEffect(() => {
    fetchExports()
    fetchDomains()
    const interval = setInterval(fetchExports, 4000)
    return () => clearInterval(interval)
  }, [fetchExports])

  useEffect(() => {
    if (form.domain_id) {
      fetchMailboxes(form.domain_id)
    } else {
      setMailboxes([])
    }
  }, [form.domain_id])

  useEffect(() => {
    const allowed = FORMATS[form.export_type] || []
    if (!allowed.find((f) => f.value === form.format)) {
      setForm((f) => ({ ...f, format: allowed[0]?.value || '' }))
    }
  }, [form.export_type])

  const handleCreate = async () => {
    if (form.export_type === 'domain_settings' && !form.domain_id) {
      toast.error('Select a domain for domain settings export')
      return
    }
    if (['emails', 'contacts'].includes(form.export_type) && !form.mailbox_id) {
      toast.error('Select a mailbox for this export type')
      return
    }

    setCreating(true)
    try {
      const payload: Record<string, any> = {
        export_type: form.export_type,
        format: form.format,
      }
      if (form.export_type === 'domain_settings') {
        payload.domain_id = form.domain_id
      } else {
        payload.mailbox_id = form.mailbox_id
      }
      if (form.date_from) payload.date_from = new Date(form.date_from).toISOString()
      if (form.date_to) payload.date_to = new Date(form.date_to).toISOString()

      const res = await api.post('/exports', payload)
      toast.success(res.data.message || 'Export queued successfully')
      setForm({
        export_type: 'emails',
        format: 'mbox',
        mailbox_id: '',
        domain_id: '',
        date_from: '',
        date_to: '',
      })
      fetchExports()
    } catch (err: any) {
      toast.error(err.response?.data?.detail?.message || 'Failed to create export')
    } finally {
      setCreating(false)
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this export job?')) return
    try {
      await api.delete(`/exports/${id}`)
      toast.success('Export deleted')
      fetchExports()
    } catch (err: any) {
      toast.error(err.response?.data?.detail?.message || 'Failed to delete export')
    }
  }

  const handleDownload = async (id: string) => {
    try {
      const res = await api.post(`/exports/${id}/download`)
      const url = res.data.download_url
      if (url && isSafeRedirectUrl(url)) {
        window.open(url, '_blank', 'noopener,noreferrer')
      } else {
        toast.error('Download URL not available or unsafe')
      }
    } catch (err: any) {
      toast.error(err.response?.data?.detail?.message || 'Failed to get download link')
    }
  }

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
    toast.success('Copied to clipboard')
  }

  const sourceLabel = (job: ExportJob) => {
    if (job.mailbox_email) return `Mailbox: ${job.mailbox_email}`
    if (job.domain_name) return `Domain: ${job.domain_name}`
    return null
  }

  const availableFormats = FORMATS[form.export_type] || []

  return (
    <div className={embedded ? 'space-y-4' : 'space-y-6'}>
      {!embedded && (
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Data Export</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Export your emails, contacts, or domain settings in open formats
          </p>
        </div>
      )}

      <Card className="border-blue-500/20 bg-blue-500/5">
        <CardContent className="p-4 flex gap-3 items-start">
          <Info className="h-5 w-5 text-blue-400 shrink-0 mt-0.5" />
          <div className="text-sm space-y-1">
            <p className="font-medium">Export limits</p>
            <ul className="text-muted-foreground list-disc list-inside">
              <li>Max export size: 10 GB per job.</li>
              <li>Download links expire after 7 days.</li>
              <li>Email/contact exports are limited to 1 per mailbox per hour.</li>
              <li>Max 3 concurrent exports per account.</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Create Export Form */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Package className="h-4 w-4 text-muted-foreground" />
            New Export
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Export Type</Label>
              <Select
                value={form.export_type}
                onValueChange={(v) => setForm((f) => ({ ...f, export_type: v, mailbox_id: '', domain_id: '' }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {EXPORT_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Format</Label>
              <Select
                value={form.format}
                onValueChange={(v) => setForm((f) => ({ ...f, format: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {availableFormats.map((f) => (
                    <SelectItem key={f.value} value={f.value}>
                      {f.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {form.export_type === 'domain_settings' && (
            <div className="space-y-2">
              <Label>Domain</Label>
              <Select
                value={form.domain_id}
                onValueChange={(v) => setForm((f) => ({ ...f, domain_id: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a domain" />
                </SelectTrigger>
                <SelectContent>
                  {domains.map((d) => (
                    <SelectItem key={d.id} value={d.id}>
                      {d.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {['emails', 'contacts'].includes(form.export_type) && (
            <>
              <div className="space-y-2">
                <Label>Domain</Label>
                <Select
                  value={form.domain_id}
                  onValueChange={(v) => setForm((f) => ({ ...f, domain_id: v, mailbox_id: '' }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a domain" />
                  </SelectTrigger>
                  <SelectContent>
                    {domains.map((d) => (
                      <SelectItem key={d.id} value={d.id}>
                        {d.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Mailbox</Label>
                <Select
                  value={form.mailbox_id}
                  onValueChange={(v) => setForm((f) => ({ ...f, mailbox_id: v }))}
                  disabled={!form.domain_id}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a mailbox" />
                  </SelectTrigger>
                  <SelectContent>
                    {mailboxes.map((m) => (
                      <SelectItem key={m.id} value={m.id}>
                        {m.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <CalendarRange className="h-3.5 w-3.5" />
                Date From (optional)
              </Label>
              <Input
                type="date"
                value={form.date_from}
                onChange={(e) => setForm((f) => ({ ...f, date_from: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <CalendarRange className="h-3.5 w-3.5" />
                Date To (optional)
              </Label>
              <Input
                type="date"
                value={form.date_to}
                onChange={(e) => setForm((f) => ({ ...f, date_to: e.target.value }))}
              />
            </div>
          </div>

          <Button
            onClick={handleCreate}
            disabled={creating}
            className="bg-gradient-to-r from-violet-500 to-purple-600"
          >
            {creating ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <FileArchive className="h-4 w-4 mr-2" />
                Start Export
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Exports List */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center justify-between">
            <span className="flex items-center gap-2">
              <RefreshCw className="h-4 w-4 text-muted-foreground" />
              Export History
            </span>
            <span className="text-xs text-muted-foreground font-normal">
              Auto-refreshes every 4s
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : exports.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground text-sm">
              No exports yet. Create your first export above.
            </div>
          ) : (
            <div className="space-y-3">
              {exports.map((ex) => (
                <div
                  key={ex.id}
                  className="flex items-start justify-between p-3 rounded-lg border bg-card/50 hover:bg-accent/40 transition-colors gap-4"
                >
                  <div className="flex-1 min-w-0 space-y-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      {statusIcon(ex.status)}
                      <span className="font-medium text-sm capitalize">{ex.export_type}</span>
                      <span className="text-xs text-muted-foreground uppercase">{ex.format}</span>
                      {statusBadge(ex.status)}
                    </div>
                    {sourceLabel(ex) && (
                      <div className="text-xs text-muted-foreground">{sourceLabel(ex)}</div>
                    )}
                    {ex.status === 'processing' && (
                      <div className="space-y-1 pt-1">
                        <div className="flex items-center gap-2">
                          <Progress value={ex.percentage} className="h-1.5 w-40" />
                          <span className="text-xs text-muted-foreground">{ex.percentage}%</span>
                        </div>
                        {ex.messages_total !== null && (
                          <div className="text-xs text-muted-foreground">
                            {ex.messages_processed} / {ex.messages_total} items processed
                          </div>
                        )}
                      </div>
                    )}
                    {ex.status === 'failed' && ex.fail_reason && (
                      <div className="flex items-start gap-2 text-xs text-red-400 pt-1">
                        <AlertCircle className="h-4 w-4 shrink-0" />
                        <span>{ex.fail_reason}</span>
                      </div>
                    )}
                    {ex.status === 'complete' && ex.file_size_mb !== undefined && (
                      <div className="text-xs text-muted-foreground pt-1">
                        Size: {formatBytes(ex.file_size_mb)} · Completed {new Date(ex.completed_at!).toLocaleString()}
                        {ex.download_expires_at && (
                          <> · Link expires {new Date(ex.download_expires_at).toLocaleString()}</>
                        )}
                      </div>
                    )}
                    <div className="text-xs text-muted-foreground">
                      Created {new Date(ex.created_at).toLocaleString()}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {ex.status === 'complete' && (
                      <>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleDownload(ex.id)}
                          title="Download"
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => setSelectedExport(ex)}
                          title="View details"
                        >
                          <ExternalLink className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                    {allowDelete && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-red-400 hover:text-red-300"
                        onClick={() => handleDelete(ex.id)}
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Details dialog */}
      <Dialog open={!!selectedExport} onOpenChange={(open) => !open && setSelectedExport(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="capitalize">{selectedExport?.export_type} export details</DialogTitle>
          </DialogHeader>
          {selectedExport && (
            <div className="space-y-3 text-sm py-2">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Status</span>
                {statusBadge(selectedExport.status)}
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Format</span>
                <span className="uppercase">{selectedExport.format}</span>
              </div>
              {sourceLabel(selectedExport) && (
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Source</span>
                  <span>{sourceLabel(selectedExport)}</span>
                </div>
              )}
              {selectedExport.file_size_mb !== undefined && (
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">File size</span>
                  <span>{formatBytes(selectedExport.file_size_mb)}</span>
                </div>
              )}
              {selectedExport.checksum_sha256 && (
                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">SHA-256 checksum</span>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => copyToClipboard(selectedExport.checksum_sha256!)}
                    >
                      <Copy className="h-3 w-3" />
                    </Button>
                  </div>
                  <code className="block text-xs bg-muted p-2 rounded break-all">
                    {selectedExport.checksum_sha256}
                  </code>
                </div>
              )}
              {selectedExport.download_expires_at && (
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Link expires</span>
                  <span>{new Date(selectedExport.download_expires_at).toLocaleString()}</span>
                </div>
              )}
              <Separator />
              {selectedExport.download_url && (
                <Button
                  className="w-full"
                  onClick={() => {
                    if (selectedExport.download_url && isSafeRedirectUrl(selectedExport.download_url)) {
                      window.open(selectedExport.download_url, '_blank', 'noopener,noreferrer')
                    } else {
                      toast.error('Download URL not available or unsafe')
                    }
                  }}
                >
                  <Download className="mr-2 h-4 w-4" />
                  Download export
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
